import * as React from 'react'
import dynamic from 'next/dynamic'
import * as ReactDOM from 'react-dom'
import { useInView } from 'react-intersection-observer'
import * as jotaiUtils from 'jotai/utils'
import * as api from '@owl-nest/api-client/next'

import { t } from '@owl-nest/localize'
import * as confetti from '@owl-nest/confetti'
import * as hooks from '@owl-nest/hooks'
import * as models from '@owl-nest/models'
import * as services from '@owl-nest/services'
import * as shadow from '@owl-nest/shadow'
import * as layout from '@ulule/layout'
import * as plume from '@ulule/owl-kit-components/next'

import { getCurrentLocale } from './router'
import { useDisplayMode } from './hooks/useDisplayMode'

import { About } from './components/About'
import { AppBackbone } from './components/AppBackbone'
import { GoalCard } from './components/GoalCard'
import { Header } from './components/Header'
import { MemberCallout } from './components/MemberCallout'
import { PostList } from './components/PostList'
import { ProjectBanner } from './components/ProjectBanner'
import { StickyNavigation } from './components/StickyNavigation'
// NOTE: This is a temporary fix. There's room for improvement with a more precise fix, by targeting the specific component that's causing hydrate issues.
const SubscriptionCarousel = dynamic(
  () => import('./components/SubscriptionCarousel').then((mod) => mod.SubscriptionCarousel),
  { ssr: false },
)

import { SuccessFreeSubscriptionModal } from './components/SuccessFreeSubscriptionModal'
import { TipBlock } from './components/TipBlock'
import { useProject } from './hooks/useProject'

type MembershipPageProps = {
  extraFields: string[]
  projectId: string | number
  projectCache: hooks.Cache<api.Project<any>>
}

export function MembershipPage({ extraFields, projectId, projectCache }: MembershipPageProps): React.ReactElement {
  const [isMounted, setIsMounted] = React.useState(false)

  jotaiUtils.useHydrateAtoms([[services.project.SHARED_PROJECT_CACHE, projectCache]], {
    dangerouslyForceHydrate: !isMounted,
  })

  React.useEffect(() => {
    setIsMounted(true)
  }, [])

  const auth = services.user.useAuth()

  const projectService = useProject(projectId, extraFields)
  const project = projectService.response.data

  const router = hooks.useNextRouter()
  const displayMode = useDisplayMode(project)
  const [isSticky, setSticky] = React.useState(false)
  const $refAnchorStickyNav = React.useRef<HTMLDivElement>(null)
  const [stickyHeight, setStickyHeight] = React.useState(0)

  const setIsProjectDetailShareLaunchModalOpen = hooks.useSetModal('ProjectDetailLaunchShare')
  const setIsProjectDetailShareModalOpen = hooks.useSetModal('ProjectDetailShare')

  const { ref: prePostListRef, inView: isPostListInView } = useInView({
    rootMargin: '300px 0px 0px 0px',
    triggerOnce: true,
  })

  const { query: queryProjectNews, response: projectNewsResponse } = useProjectNews(project)

  React.useEffect(() => {
    if (isPostListInView && auth.type !== 'initial') {
      queryProjectNews()
    }
  }, [auth.type, isPostListInView])

  const currentLocale = getCurrentLocale(router)
  const isEditMode = displayMode === 'edit'
  const projectTitle = models.project.name(project, currentLocale)
  const memberships = project.rewards?.filter((reward) => !reward.is_hidden)
  const isMember = models.project.isUserMember(project)

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.location.search) {
      const params = new URLSearchParams(document.location.search)

      const confettiParameter = params.get('confetti')
      const launchParameter = params.get('launch')

      if (confettiParameter === 'true') {
        confetti.renderConfetti()
        params.delete('confetti')
        window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`)
      }

      if (launchParameter) {
        setIsProjectDetailShareLaunchModalOpen(true)
      }
    }
  }, [])

  React.useEffect(() => {
    const handle = setInterval(() => {
      if ($refAnchorStickyNav.current) {
        clearInterval(handle)
        setStickyHeight($refAnchorStickyNav.current.getBoundingClientRect().top + document.documentElement.scrollTop - 25)
      }
    }, 100)

    return () => clearInterval(handle)
  }, [])

  const handleScroll = React.useCallback(() => {
    setSticky(window.scrollY > stickyHeight - (isEditMode ? 113 : 60))
  }, [stickyHeight, isEditMode])

  React.useEffect(() => {
    window.addEventListener('scroll', handleScroll)

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const modalContainer = React.useMemo(() => {
    if (router.isReady && typeof window !== 'undefined') {
      return document.getElementById('modal-container') ?? document.body
    }
  }, [router.isReady])

  const isFreeMember = isMember && models.project.getActiveMembershipOrder(project)?.reward?.is_free

  return (
    <AppBackbone project={project} stickyUntil={stickyHeight}>
      <StickyNavigation isSticky={isSticky} project={project} />

      {project.show_latest_project_online && project.latest_project_online && (
        <ProjectBanner owner={project.owner} project={project} latestProject={project.latest_project_online} />
      )}

      <layout.modals.ShareProject
        closable={false}
        enableWidget={false}
        project={project}
        modalId="ProjectDetailLaunchShare"
        modalButtons={[
          {
            type: 'button',
            props: {
              kind: 'primary',
              type: 'button',
              onClick: () => {
                const params = new URLSearchParams(document.location.search)
                params.delete('launch')
                window.history.replaceState(null, '', `${window.location.pathname}?${params.toString()}`)
                setIsProjectDetailShareLaunchModalOpen(false)
              },
              children: t('See my page'),
            },
          },
        ]}
        modalHero={
          <>
            <plume.styles.heading.L>{t('Congratulations, you’re live!')}</plume.styles.heading.L>
            <plume.styles.copy.M>
              {t(
                'Your campaign starts now: share your project with your first circle to to raise the gauge and launch this adventure.',
              )}
            </plume.styles.copy.M>
          </>
        }
        modalPastille={<plume.illustrations.twoToned.Rocket size={90} />}
        sharingContext="membership-project-page"
        trackingConfig={{
          category: shadow.CATEGORY.MEMBERSHIP_PAGE,
          source: 'membership_project_page',
        }}
      />
      <Header
        avatar={models.project.avatarImage(project, currentLocale, '290x290')}
        backgroundImage={models.project.mainImage(project, '2x', currentLocale)}
        bio={models.project.ownerDescription(project, currentLocale)}
        name={projectTitle ? projectTitle : t('My page')}
        project={project}
        setShareModalOpen={setIsProjectDetailShareModalOpen}
      />

      {project.show_goal && <GoalCard project={project} />}

      {isMember && <MemberCallout project={project} />}

      <div ref={$refAnchorStickyNav} />

      {(!isMember || isFreeMember) && (
        <>
          <SubscriptionCarousel
            project={project}
            memberships={memberships}
            activeMembershipId={models.project.getActiveMembershipOrder(project)?.reward?.id}
            extraFields={extraFields}
          />
          {project.donations_single_allowed && <TipBlock project={project} />}
        </>
      )}

      <div ref={prePostListRef} />

      {(isEditMode || (projectNewsResponse.data?.meta.total_count ?? 0) > 0) && (
        <PostList project={project} projectNewsResponse={projectNewsResponse} />
      )}

      <About project={project} />

      <SuccessFreeSubscriptionModal project={project} />

      {isMember && !isFreeMember && (
        <>
          <SubscriptionCarousel
            project={project}
            activeMembershipId={models.project.getActiveMembershipOrder(project)?.reward?.id}
            memberships={memberships}
            extraFields={extraFields}
          />
          {project.donations_single_allowed && <TipBlock project={project} />}
        </>
      )}

      {modalContainer &&
        ReactDOM.createPortal(
          <layout.modals.ShareProject
            enableWidget={true}
            project={project}
            sharingContext="membership-project-page"
            trackingConfig={{
              category: shadow.CATEGORY.MEMBERSHIP_PAGE,
              source: 'membership_page',
            }}
          />,
          modalContainer,
        )}
    </AppBackbone>
  )
}

function useProjectNews(project: models.project.ProjectForOwner): {
  query: () => void
  response: hooks.Response<api.NewsListPage>
} {
  const auth = services.user.useAuth()

  const hasUserNewsReadPermission = project.permissions.news?.includes(api.ProjectNewsPermission.READ) ?? false

  const useProjectNewsParams: services.news.UseProjectNewsParams = {
    limit: 3,
    sort: 'created_at',
  }

  if (hasUserNewsReadPermission) {
    useProjectNewsParams.status = 'online,waiting'
  } else {
    useProjectNewsParams.status = 'online'
  }

  const { query, response } = services.news.useProjectNews(project.id, {
    fetchOnMount: false,
    params: useProjectNewsParams,
    withToken: auth.type === 'loggedin',
  })

  return { query, response }
}
